import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Clock,
  DollarSign,
  Phone,
  Mail,
  User,
  Send,
  Bot,
  Loader2,
  Share2,
  Heart,
  Briefcase,
  Home,
  Car,
  Tag,
  Calendar,
  Newspaper,
  ExternalLink,
  Building,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI Hero images for each listing type
const heroImages = {
  job_listing: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/ydox3cgb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_17_51%20PM.png",
  coupon: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/rjb518jb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_23_26%20PM.png",
  event: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/2kvq0aw3_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_31_44%20PM.png",
  real_estate: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/xs0rcc5u_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_28_31%20PM.png",
  vehicle: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/6l02z6bb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_35_53%20PM.png",
  classified: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/s1iytthq_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_40_22%20PM.png",
};

// Hero names for each type
const heroNames = {
  job_listing: "JORDAN",
  coupon: "MAYA",
  event: "ELENA",
  real_estate: "MARCUS",
  vehicle: "TONY",
  classified: "SAGE",
};

// Icons for each type
const typeIcons = {
  job_listing: Briefcase,
  coupon: Tag,
  event: Calendar,
  real_estate: Home,
  vehicle: Car,
  classified: Newspaper,
};

// Colors for each type
const typeColors = {
  job_listing: "#00E5FF",
  coupon: "#10B981",
  event: "#F59E0B",
  real_estate: "#EC4899",
  vehicle: "#EF4444",
  classified: "#8B5CF6",
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sessionId] = useState(() => `listing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  // Scroll to top immediately on mount/navigation - use useLayoutEffect for synchronous scroll
  useLayoutEffect(() => {
    // Temporarily disable smooth scroll behavior
    const htmlElement = document.documentElement;
    htmlElement.style.scrollBehavior = 'auto';
    
    window.scrollTo(0, 0);
    htmlElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Restore after brief delay
    const timeout = setTimeout(() => {
      htmlElement.style.scrollBehavior = '';
    }, 100);
    
    return () => clearTimeout(timeout);
  }, [id]);

  useEffect(() => {
    const fetchListing = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/content/${id}`);
        setListing(res.data);
        
        // Initialize chat with greeting
        const heroName = heroNames[res.data.content_type] || "AI Assistant";
        const color = typeColors[res.data.content_type] || "#7C3AED";
        setMessages([
          {
            role: "assistant",
            content: `Hi! I'm ${heroName}, your AI assistant for this listing. I can answer any questions you have about "${res.data.title}" and help you connect with the seller. What would you like to know?`,
          },
        ]);
      } catch (err) {
        console.error("Error fetching listing:", err);
        setError("Listing not found");
      } finally {
        setIsLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  useEffect(() => {
    // Only scroll within the chat container, not the whole page
    if (messages.length > 1) {  // Only auto-scroll after initial greeting
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsSending(true);

    try {
      const res = await axios.post(`${API}/chat/listing-detail`, {
        message: userMessage,
        listing_id: id,
        listing_title: listing?.title,
        listing_type: listing?.content_type,
        seller_name: listing?.author_name || listing?.business_name,
        session_id: sessionId,
      });

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.response },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I'm having trouble connecting right now. Please try again in a moment.",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="page-container min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-white text-2xl font-bold mb-4">Listing Not Found</h1>
        <Link to="/marketplace">
          <Button className="btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Marketplace
          </Button>
        </Link>
      </div>
    );
  }

  const TypeIcon = typeIcons[listing.content_type] || Newspaper;
  const color = typeColors[listing.content_type] || "#7C3AED";
  const heroImage = heroImages[listing.content_type] || heroImages.classified;
  const heroName = heroNames[listing.content_type] || "AI Assistant";

  return (
    <div className="page-container min-h-screen" data-testid="listing-detail-page">
      {/* Hero Section */}
      <section className="relative py-8 hero-pattern">
        <div className="hero-glow top-0 right-1/4 opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Button */}
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </Link>

          {/* Listing Header */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Image */}
            <div className="lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative rounded-2xl overflow-hidden"
              >
                <img
                  src={listing.thumbnail_url || "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800"}
                  alt={listing.title}
                  className="w-full h-[400px] object-cover"
                />
                <Badge
                  className="absolute top-4 left-4 text-white"
                  style={{ backgroundColor: color }}
                >
                  <TypeIcon className="w-3 h-3 mr-1" />
                  {listing.content_type?.replace("_", " ") || "Listing"}
                </Badge>
              </motion.div>
            </div>

            {/* Details */}
            <div className="lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
                  {listing.title}
                </h1>

                {/* Seller Info */}
                <div className="flex items-center gap-4 mb-6">
                  {listing.business_name && (
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <Building className="w-4 h-4" />
                      <span>{listing.business_name}</span>
                    </div>
                  )}
                  {listing.author_name && (
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <User className="w-4 h-4" />
                      <span>{listing.author_name}</span>
                    </div>
                  )}
                </div>

                {/* Meta Info */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <Clock className="w-4 h-4" />
                    <span>Posted {listing.created_at ? new Date(listing.created_at).toLocaleDateString() : "Recently"}</span>
                  </div>
                  {listing.location && (
                    <div className="flex items-center gap-2 text-[#94A3B8]">
                      <MapPin className="w-4 h-4" />
                      <span>{listing.location}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="glass-card p-6 rounded-xl mb-6">
                  <h3 className="text-white font-semibold mb-3">Description</h3>
                  <p className="text-[#94A3B8] whitespace-pre-wrap">{listing.description}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    className="flex-1"
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      document.getElementById('chat-input')?.focus();
                    }}
                  >
                    <Send className="w-4 h-4 mr-2" /> Message Seller
                  </Button>
                  <Button variant="outline" className="btn-secondary">
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </Button>
                  <Button variant="outline" className="btn-secondary">
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Chat Box */}
            <div className="lg:col-span-2 min-w-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl overflow-hidden"
              >
                {/* Chat Header */}
                <div
                  className="p-4 flex items-center gap-4 border-b border-white/10"
                  style={{ background: `linear-gradient(135deg, ${color}20, transparent)` }}
                >
                  <img
                    src={heroImage}
                    alt={heroName}
                    className="w-14 h-14 rounded-full border-2 object-cover"
                    style={{ borderColor: color }}
                  />
                  <div className="flex-1">
                    <h3 className="text-white font-bold">{heroName}</h3>
                    <p className="text-sm" style={{ color }}>
                      AI {listing.content_type?.replace("_", " ")} Assistant • Online
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="h-[400px] overflow-y-auto p-4 space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-2xl ${
                          msg.role === "user"
                            ? "bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white"
                            : "glass-card text-white"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="glass-card p-4 rounded-2xl">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color }} />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-3">
                    <Input
                      id="chat-input"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                      placeholder="Ask about this listing or message the seller..."
                      className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                      data-testid="listing-chat-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={isSending || !inputMessage.trim()}
                      style={{ backgroundColor: color }}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[#64748B] text-xs mt-2">
                    Messages are sent to the seller. They'll receive your inquiry in their dashboard.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Seller Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6 rounded-xl"
              >
                <h3 className="text-white font-semibold mb-4">Seller Information</h3>
                <div className="space-y-3">
                  {listing.business_name && (
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5" style={{ color }} />
                      <span className="text-white">{listing.business_name}</span>
                    </div>
                  )}
                  {listing.author_name && (
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5" style={{ color }} />
                      <span className="text-white">{listing.author_name}</span>
                    </div>
                  )}
                  {listing.contact_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5" style={{ color }} />
                      <span className="text-[#94A3B8]">{listing.contact_email}</span>
                    </div>
                  )}
                  {listing.contact_phone && (
                    <a href={`tel:${listing.contact_phone}`} className="flex items-center gap-3 hover:text-white">
                      <Phone className="w-5 h-5" style={{ color }} />
                      <span className="text-[#94A3B8]">{listing.contact_phone}</span>
                    </a>
                  )}
                </div>
              </motion.div>

              {/* AI Hero Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card p-6 rounded-xl text-center"
                style={{ borderColor: `${color}30`, borderWidth: 1 }}
              >
                <img
                  src={heroImage}
                  alt={heroName}
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-2 object-cover"
                  style={{ borderColor: color }}
                />
                <h3 className="text-white font-bold text-lg">{heroName}</h3>
                <p className="text-sm mb-4" style={{ color }}>
                  AI {listing.content_type?.replace("_", " ")} Hero™
                </p>
                <p className="text-[#94A3B8] text-sm">
                  I'm here to help you with questions about this listing and connect you with the seller.
                </p>
              </motion.div>

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-card p-6 rounded-xl"
              >
                <h3 className="text-white font-semibold mb-4">Quick Questions</h3>
                <div className="space-y-2">
                  {[
                    "Is this still available?",
                    "Can I get more details?",
                    "What's the best price?",
                    "How can I contact the seller?",
                  ].map((question, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setInputMessage(question);
                        document.getElementById('chat-input')?.focus();
                      }}
                      className="w-full text-left px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
